/**
 * The word behind a control whose whole label is a glyph.
 *
 * ── Why this exists beside `HintPopover`, which is not it ────────────────────────────────────
 * `HintPopover` is the ⓘ: a sentence of EXPLANATION, opened by a press, because an explanation
 * nobody can reach without a mouse is an explanation that does not exist. Its own header argues
 * against tooltips for exactly that reason and the argument still holds.
 *
 * This is the other thing — the control's own NAME, on a button that draws no word. The name is
 * never only here: `aria-label` carries it for assistive technology, and this adds the channel a
 * sighted pointer user has and a screen reader does not. Base UI opens it on hover AND on keyboard
 * focus, which is what a bare `title` does not do — `title` never fires on focus, so a trainer
 * tabbing to a pencil met an unlabelled square.
 *
 * ── The trigger is the caller's own button ───────────────────────────────────────────────────
 * `render` hands Base UI the element rather than wrapping it, so the button keeps its own class,
 * its own handlers and its own `disabled`/`aria-disabled` state, and nothing new appears in the
 * DOM around it. A tooltip that wrapped its trigger would put a `<span>` between the panel's footer
 * flex row and the control it lays out.
 *
 * The surface is `MenuSurface`'s, worn rather than transcribed — this app has one popup — with only
 * the three geometry values a one-line label needs.
 */

import { Tooltip } from '@base-ui/react/tooltip';

import menuStyles from './MenuSurface.module.css';
import styles from './IconTooltip.module.css';

export interface IconTooltipProps {
  /** The control's own name, and the same string its `aria-label` carries. */
  readonly label: string;
  /** The button itself. It keeps its own class, handlers and state. */
  readonly children: React.ReactElement<Record<string, unknown>>;
}

export function IconTooltip({ label, children }: IconTooltipProps) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger render={children} />
      <Tooltip.Portal>
        <Tooltip.Positioner className={styles.positioner} sideOffset={8}>
          {/* `aria-hidden`: the trigger's `aria-label` already says this, and Base UI would
            * otherwise describe the button with its own name a second time. */}
          <Tooltip.Popup
            className={`${menuStyles.popup} ${styles.popup}`}
            aria-hidden="true"
          >
            {label}
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
